import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Boxes, Check, PackageMinus, Pencil, X } from 'lucide-react'
import { toast } from 'sonner'

import type { ScentingLotOutReason } from '@/lib/api/scenting-lot-api'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { WarehouseNoteStatusBadge } from '@/components/warehouse/note-status-badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useApproveScentingOutNote,
    useCancelScentingOutNote,
    useRejectScentingOutNote,
    useRollbackScentingOutNote,
    useScentingOutNote,
} from '@/hooks/use-scenting-lot-query'
import {
    NoteRollbackButton,
    NoteRollbackLinks,
} from '@/components/warehouse/note-rollback'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/scenting-warehouse/stock-out-notes/$id',
)({
    component: StockNoteDetailPage,
})

export function StockNoteDetailPage() {
    const { id } = Route.useParams()
    const { data, isLoading } = useScentingOutNote(id)
    const note = data?.data
    const { user, hasPermission } = useAuth()
    const approve = useApproveScentingOutNote()
    const reject = useRejectScentingOutNote()
    const cancel = useCancelScentingOutNote()
    const rollback = useRollbackScentingOutNote()
    const [rejectOpen, setRejectOpen] = useState(false)
    const [rejectReason, setRejectReason] = useState('')
    const approveWorkflow = useWorkflowTracking('scenting_stock_out', {
        operation: 'approve',
        attemptKey: id,
        autoStart: false,
    })
    const rejectWorkflow = useWorkflowTracking('scenting_stock_out', {
        operation: 'reject',
        attemptKey: id,
        active: rejectOpen,
    })
    const cancelWorkflow = useWorkflowTracking('scenting_stock_out', {
        operation: 'cancel',
        attemptKey: id,
        autoStart: false,
    })

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isPending = note.status === 'pending'
    const isApproved = note.status === 'approved'
    const isForPackaging = note.reason === 'for_packaging'
    const isCreator = user?.id && note.created_by_id === user.id
    // Quyền phải khớp ĐÚNG quyền route backend đòi cho kho này. Trước đây màn
    // này đọc nhầm quyền kho PHÂN LOẠI (sorting_lots.*): người đúng quyền không
    // thấy nút, người sai quyền bấm vào thì ăn 403 từ server.
    const canApprove = hasPermission('scenting_lots.approve_out')
    const canWrite = hasPermission('scenting_lots.write')
    // PXK đã có PNK tiếp nhận thì ẩn nút — form tạo sẽ luôn bị backend từ chối
    // (1 PXK = 1 PNK).
    const alreadyReceived = note.has_active_in_note === true
    const canCreateProductPNK =
        isApproved &&
        isForPackaging &&
        !alreadyReceived &&
        hasPermission('warehouse_sales.manage')

    const handleApprove = async () => {
        approveWorkflow.step('warning_shown')
        if (!confirm(m.sl_detail_confirm_approve_out())) {
            approveWorkflow.finish('closed')
            return
        }
        approveWorkflow.step('warning_confirmed')
        try {
            await approveWorkflow.run(() => approve.mutateAsync(id))
        } catch (err) {
            toast.error(
                err instanceof Error
                    ? err.message
                    : m.sl_detail_error_approve(),
            )
        }
    }

    const handleReject = async () => {
        try {
            await rejectWorkflow.run(() =>
                reject.mutateAsync({ id, reason: rejectReason }),
            )
            setRejectOpen(false)
            setRejectReason('')
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_reject(),
            )
        }
    }

    const handleCancel = async () => {
        cancelWorkflow.step('warning_shown')
        if (!confirm(m.sl_detail_confirm_cancel_out())) {
            cancelWorkflow.finish('closed')
            return
        }
        cancelWorkflow.step('warning_confirmed')
        try {
            await cancelWorkflow.run(() => cancel.mutateAsync(id))
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_cancel(),
            )
        }
    }

    return (
        <div className="space-y-4 page-pad">
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <PageBackButton asChild>
                                <Link
                                    to="/scenting-warehouse/stock-out-notes"
                                    search={{ page: 1 }}
                                >
                                    <ArrowLeft className="size-4" />
                                </Link>
                            </PageBackButton>
                            <PackageMinus className="size-5" />
                            <CardTitle className="font-mono text-base md:text-2xl">
                                {note.code}
                            </CardTitle>
                            <WarehouseNoteStatusBadge status={note.status} />
                        </div>
                        {isPending && (
                            <div className="flex gap-2 flex-wrap">
                                {isCreator &&
                                    canWrite &&
                                    !note.stocktake_session_id && (
                                        <Button
                                            asChild
                                            variant="outline"
                                            className="h-11 md:h-9"
                                        >
                                            <Link
                                                to="/scenting-warehouse/stock-out-notes/$id/edit"
                                                params={{ id }}
                                            >
                                                <Pencil className="size-4 mr-1" />
                                                {m.pl_pnk_edit_btn()}
                                            </Link>
                                        </Button>
                                    )}
                                {canApprove && (
                                    <>
                                        <Button
                                            className="h-11 md:h-9"
                                            onClick={handleApprove}
                                            disabled={approve.isPending}
                                        >
                                            <Check className="size-4 mr-1" />{' '}
                                            {m.sl_detail_action_approve()}
                                        </Button>
                                        <Button
                                            className="h-11 md:h-9"
                                            variant="destructive"
                                            onClick={() => setRejectOpen(true)}
                                        >
                                            <X className="size-4 mr-1" />{' '}
                                            {m.sl_detail_action_reject()}
                                        </Button>
                                    </>
                                )}
                                {isCreator && canWrite && (
                                    <Button
                                        className="h-11 md:h-9"
                                        variant="outline"
                                        onClick={handleCancel}
                                        disabled={cancel.isPending}
                                    >
                                        {m.sl_detail_action_cancel()}
                                    </Button>
                                )}
                            </div>
                        )}
                        {canCreateProductPNK && (
                            <Button asChild className="h-11 md:h-9">
                                <Link
                                    to="/sales-warehouse/stock-in-notes/new"
                                    search={{ pxk: note.id }}
                                >
                                    <Boxes className="size-4 mr-1" />
                                    {m.scl_out_action_create_product_pnk()}
                                </Link>
                            </Button>
                        )}
                        <NoteRollbackButton
                            noteId={note.id}
                            status={note.status}
                            canRollback={canApprove}
                            rolledBackBy={note.rolled_back_by}
                            rollbackOf={note.rollback_of}
                            confirmMessage={m.note_rollback_confirm_out()}
                            pending={rollback.isPending}
                            onRollback={() => rollback.mutateAsync(id)}
                        />
                        {alreadyReceived && (
                            <p className="text-sm text-muted-foreground self-center">
                                {m.pxk_already_received_note()}
                            </p>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="grid gap-3 grid-cols-2">
                    <div className="col-span-2">
                        <Field
                            label={m.sl_detail_label_reason()}
                            value={reasonLabel(note.reason)}
                        />
                    </div>
                    <Field
                        label={m.sl_detail_label_created_at()}
                        value={formatDateTimeVN(note.created_at)}
                    />
                    <Field
                        label={m.sl_detail_label_created_by()}
                        value={note.created_by_name ?? '—'}
                    />
                    {note.approved_at && (
                        <>
                            <Field
                                label={m.sl_detail_label_approved_at()}
                                value={formatDateTimeVN(note.approved_at)}
                            />
                            <Field
                                label={
                                    note.status === 'approved'
                                        ? m.sl_detail_label_approver()
                                        : m.sl_detail_label_rejecter()
                                }
                                value={note.approved_by_name ?? '—'}
                            />
                        </>
                    )}
                    {note.cancelled_at && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_cancelled_at()}
                                value={formatDateTimeVN(note.cancelled_at)}
                            />
                        </div>
                    )}
                    {note.notes && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_notes()}
                                value={note.notes}
                            />
                        </div>
                    )}
                    {note.rejected_reason && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_rejected_reason()}
                                value={note.rejected_reason}
                            />
                        </div>
                    )}
                    <NoteRollbackLinks
                        rolledBackBy={note.rolled_back_by}
                        rollbackOf={note.rollback_of}
                        renderLink={(ref) => (
                            <Link
                                to="/scenting-warehouse/stock-in-notes/$id"
                                params={{ id: ref.id }}
                                className="text-primary hover:underline"
                            >
                                {ref.code}
                            </Link>
                        )}
                    />
                </CardContent>
            </Card>

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_lots_col_code()}</TableHead>
                            <TableHead>{m.sl_lot_grade()}</TableHead>
                            <TableHead className="text-right">
                                {m.sl_detail_col_out()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(note.items ?? []).map((it) => (
                            <TableRow key={it.id}>
                                <TableCell className="font-mono text-sm">
                                    <Link
                                        to="/scenting-warehouse/lots/$id"
                                        params={{ id: it.scenting_lot_id }}
                                        className="text-primary hover:underline"
                                    >
                                        {it.scenting_lot_code}
                                    </Link>
                                </TableCell>
                                <TableCell>{it.grade_name ?? '—'}</TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {formatQty(it.quantity)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.sl_detail_dialog_reject_title_out()}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label>{m.sl_detail_dialog_reject_label()}</Label>
                        <Input
                            value={rejectReason}
                            onChange={(e) => {
                                rejectWorkflow.interact('details')
                                setRejectReason(e.target.value)
                            }}
                            placeholder={m.sl_detail_dialog_reject_placeholder()}
                        />
                    </div>
                    <DialogFooter className="flex-row gap-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1 sm:h-10 sm:flex-none"
                            onClick={() => setRejectOpen(false)}
                        >
                            {m.sl_detail_dialog_cancel_btn()}
                        </Button>
                        <Button
                            variant="destructive"
                            className="h-11 flex-1 sm:h-10 sm:flex-none"
                            onClick={handleReject}
                            disabled={reject.isPending}
                        >
                            {m.sl_detail_dialog_confirm_reject_btn()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

function reasonLabel(reason: ScentingLotOutReason): string {
    if (reason === 'for_packaging') return m.scl_out_reason_for_packaging()
    if (reason === 'discard') return m.scl_out_reason_discard()
    return m.scl_out_reason_adjustment()
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value}</p>
        </div>
    )
}
